import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid2,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	createNewWorkCenter,
	updateWorkCenterData,
} from "../../../../../../export/gat-admin";
import LoadingButton from "../../../../../design/wilya-loading-button";
import { CompanyLocation } from "../../../store/gat-admin-slice-types";

interface IWorkCenterProps {
	creatingNewWorkCenter: boolean;
	workCenterData: any;
	setWorkCenterData: React.Dispatch<React.SetStateAction<any>>;
	onClose: () => void;
	shiftPatterns: { id: number; name: string }[];
}
const WorkCenterDialog: React.FC<IWorkCenterProps> = ({
	creatingNewWorkCenter,
	workCenterData,
	setWorkCenterData,
	onClose,
	shiftPatterns,
}) => {
	const dispatch = useDispatch();
	const [initialWorkCenterData, setInitialWorkCenterData] = useState({});
	// to show custom placeholder in select
	const [isFocused, setIsFocused] = useState(false);
	const createNewWorkCenterStatus = useSelector(
		(state: any) => state.gatAdminConfiguration.createNewWorkCenter.status
	);
	const updateWorkCenterStatus = useSelector(
		(state: any) => state.gatAdminConfiguration.updateWorkCenterData.status
	);
	const openWorkCenterDialog = useSelector(
		(state: any) => state.gatAdminConfiguration.openWorkCenterDialog
	);
	const locations = useSelector(
		(state: any) => state.gatAdminConfiguration.locations
	);
	const currentLocationData = useSelector(
		(state: any) => state.gatAdminConfiguration.currentLocationData
	);

	useEffect(() => {
		return () => {
			onClose();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!Object.prototype.hasOwnProperty.call(workCenterData, "locationId")) {
			if (workCenterData.locationId !== 0 && locations) {
				locations.forEach((location: CompanyLocation) => {
					location.workCenters.forEach((wc) => {
						if (wc.id === workCenterData.id) {
							const workCenterDataWithLocationId = {
								...workCenterData,
								locationId: location.id,
							};
							setWorkCenterData(workCenterDataWithLocationId);
							if (
								JSON.stringify(initialWorkCenterData) === "{}" &&
								JSON.stringify(workCenterDataWithLocationId) !== "{}"
							) {
								setInitialWorkCenterData(workCenterDataWithLocationId);
							}
						}
					});
				});
			}
		}
	}, [workCenterData, locations, setWorkCenterData, initialWorkCenterData]);

	const closeWorkCenterDialog = () => {
		setInitialWorkCenterData({});
		onClose();
	};

	return (
		<Dialog
			onClose={() => closeWorkCenterDialog()}
			aria-labelledby='add-new-work-center'
			open={openWorkCenterDialog}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle
				id='work-center-dialog-title'
			>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontSize: "20px",
						fontWeight: 500,
						color: "rgba(0, 0, 0, 0.87)",
						paddingTop: "29px",
						paddingLeft: "21px",
						paddingBottom: "9px",
						paddingRight: "21px",
					}}
				>
					{creatingNewWorkCenter ? "Add" : "Edit"} Work Center
				</Typography>
			</DialogTitle>
			<Divider />
			<DialogContent>
				<Box
				sx={{
						paddingLeft: "21px",
					}}
				>
					<Typography
							sx={{
								fontFamily: "Roboto",
								fontSize: "16px",
								color: "rgba(0, 0, 0, 0.6)",
								fontWeight: 400,
							}}
						>
							Location - {currentLocationData?.name ?? "-"}
						</Typography>
				</Box>
				<Grid2
					container
					sx={{
						paddingTop: "12px",
						paddingLeft: "21px",
						paddingRight: "21px",
						paddingBottom: "16px",
					}}
					spacing={4}
					direction={"column"}
				>
					<Grid2>
						<TextField
							label='Work Center Name'
							value={workCenterData.name ?? ""}
							slotProps={{
								input: {
									inputProps: {
										maxLength: 50,
									}
								}
							}}
							onChange={(e) =>
								setWorkCenterData({ ...workCenterData, name: e.target.value })
							}
							variant='filled'
							fullWidth
						/>
					</Grid2>
					<Grid2>
						<FormControl fullWidth variant='filled'>
							<InputLabel id='shift-patterns-select-label'>
								Shift Patterns
							</InputLabel>
							<Select
								labelId='shift-patterns-select-label'
								id='shift-patterns-select'
								multiple
								value={workCenterData.shiftPatternIds || []}
								displayEmpty
								onFocus={() => setIsFocused(true)}
								onBlur={() => setIsFocused(false)}
								onChange={(e) => {
									const {
										target: { value },
									} = e;
									if (value[value.length - 1] === "all") {
										setWorkCenterData({
											...workCenterData,
											shiftPatternIds:
												workCenterData.shiftPatternIds?.length ===
												shiftPatterns.length
													? []
													: shiftPatterns.map((sp) => sp.id),
										});
										return;
									}
									setWorkCenterData({
										...workCenterData,
										shiftPatternIds:
											typeof value === "string" ? value.split(",") : value,
									});
								}}
								renderValue={(selected) => {
									if (selected.length > 0) {
										return (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{shiftPatterns
													.filter((sp) => selected.includes(sp.id))
													.map((sp) => (
														<Chip key={sp.id} label={sp.name} />
													))}
											</Box>
										);
									}
									if (isFocused) {
										return (
											<Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
												Select the Shifts in which this Work Center operates
											</Typography>
										);
									}
									return null;
								}}
								MenuProps={{
									PaperProps: {
										style: {
											maxHeight: 224,
											width: 250,
										},
									},
								}}
							>
								{shiftPatterns.length === 0 ? (
									<MenuItem disabled>
										<ListItemText>
											Setup Shift Patterns to map to Work Center
										</ListItemText>
									</MenuItem>
								) : (
									[
										<MenuItem key="all" value='all'>
											<Checkbox
												checked={
													shiftPatterns.length > 0 &&
													workCenterData.shiftPatternIds?.length ===
														shiftPatterns.length
												}
												indeterminate={
													workCenterData.shiftPatternIds?.length > 0 &&
													workCenterData.shiftPatternIds?.length <
														shiftPatterns.length
												}
											/>
											<ListItemText primary='Select All' />
										</MenuItem>,
										shiftPatterns.map((sp) => (
											<MenuItem key={sp.id} value={sp.id}>
												<Checkbox checked={workCenterData.shiftPatternIds?.indexOf(sp.id) > -1} />
												<ListItemText primary={sp.name} />
											</MenuItem>
										))
									]
								)}
							</Select>
						</FormControl>
					</Grid2>
				</Grid2>
			</DialogContent>
			<Divider />
			<DialogActions>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "flex-end",
						marginTop: "16px",
						marginBottom: "16px",
						marginRight: "21px",
					}}
				>
					<Button variant='outlined' onClick={() => closeWorkCenterDialog()}>
						Cancel
					</Button>
					<LoadingButton
						variant='contained'
						disabled={
							workCenterData.name === "" ||
							JSON.stringify(workCenterData) ===
								JSON.stringify(initialWorkCenterData)
						}
						sx={{ marginLeft: "16px" }}
						loading={
							createNewWorkCenterStatus === "pending" ||
							updateWorkCenterStatus === "pending"
						}
						onClick={() => {
							if (creatingNewWorkCenter) {
								dispatch(createNewWorkCenter(workCenterData));
							} else {
								dispatch(updateWorkCenterData(workCenterData));
							}
						}}
						loadingIndicator={
							<CircularProgress
								size={16}
								sx={{ color: "rgba(0, 0, 0, 0.38)" }}
							/>
						}
					>
						{creatingNewWorkCenter ? "CREATE" : "SAVE"}
					</LoadingButton>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default React.memo(WorkCenterDialog);
